<template>
  <page-option disable-add overflow-hidden>
    <template #title>
      <v-avatar color="#E3FCF7" size="35">
        <v-icon color="#108F6E">mdi-image-multiple-outline</v-icon>
      </v-avatar>
      <div class="pl-2 title-page">{{ $t('sidebar.raster-data.name') }}</div>
    </template>
    <template #below-toolbar>
      <FilterSearch
        v-model="params"
        disable-create
        can-show-in-search
        placeholder="Tìm kiếm theo tên ảnh"
      >
        <div class="d-flex flex-sm-row flex-column flex-wrap">
          <div class="filter-item">
            <field-group
              v-model="params.type"
              background-color="#FFFFFF"
              dense
              :items="typeItems"
              label="Loại"
              no-label
              outlined
              hideDetails
              typeInput="v-select"
              item-text="text"
              item-value="value"
              clearable
            />
          </div>
          <div class="filter-item">
            <field-group
              v-model="params.raster_type_id"
              background-color="#FFFFFF"
              dense
              :items="rasterTypes"
              :loading="loadingTypes"
              label="Loại ảnh viễn thám"
              no-label
              outlined
              hideDetails
              typeInput="v-autocomplete"
              item-text="name"
              item-value="id"
              clearable
            />
          </div>
          <div class="filter-item">
            <field-group
              v-model="params.raster_source_id"
              background-color="#FFFFFF"
              dense
              :items="rasterSources"
              :loading="loadingSources"
              label="Nguồn dữ liệu"
              no-label
              outlined
              hideDetails
              typeInput="v-autocomplete"
              item-text="name"
              item-value="id"
              clearable
            />
          </div>
          <div class="filter-item">
            <field-group
              v-model="params.workspace_id"
              background-color="#FFFFFF"
              dense
              :items="workspaces"
              :loading="loadingWorkspaces"
              :label="$t('sidebar.workspace.name')"
              no-label
              outlined
              hideDetails
              typeInput="v-autocomplete"
              item-text="name"
              item-value="id"
              clearable
            />
          </div>
        </div>
        <template #extra-btn>
          <div class="pt-1 pl-2">
            <v-btn
              class="text-none"
              color="primary"
              outlined
              @click="openAdvanced"
            >
              <v-icon left small>mdi-filter-variant</v-icon>
              Tìm kiếm nâng cao
              <v-chip
                v-if="activeAdvancedCount"
                class="ml-2"
                color="primary"
                outlined
                x-small
              >
                {{ activeAdvancedCount }}
              </v-chip>
            </v-btn>
          </div>
        </template>
      </FilterSearch>
    </template>
    <v-sheet class="fillheight overflow-hidden">
      <base-serve-table
        ref="table"
        :api="api"
        :headers="headers"
        :params="tableParams"
        :defaultParams="default_params"
        @update:items="onItems"
        @update:loading="onTableLoading"
      >
        <template v-slot:[`item.type`]="{ item }">
          {{ typeLabel(item.type) }}
        </template>
        <template v-slot:[`item.raster_type`]="{ item }">
          {{ rasterTypeName(item) || '—' }}
        </template>
        <template v-slot:[`item.raster_source`]="{ item }">
          {{ rasterSourceName(item) || '—' }}
        </template>
        <template v-slot:[`item.workspace`]="{ item }">
          {{ (item.workspace && item.workspace.name) || '—' }}
        </template>
        <template v-slot:[`item.metadata`]="{ item }">
          <div v-if="metadataRows(item).length" class="py-1">
            <v-chip
              v-for="(row, index) in metadataRows(item).slice(0, 3)"
              :key="row.id || index"
              class="ma-1"
              label
              outlined
              small
            >
              {{ row.field }}: {{ row.value }}
            </v-chip>
            <span
              v-if="metadataRows(item).length > 3"
              class="text-caption grey--text"
            >
              +{{ metadataRows(item).length - 3 }}
            </span>
          </div>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:[`item.created_at`]="{ item }">
          <span>{{ item.created_at | dateTime('short') }}</span>
        </template>
        <template v-slot:[`item.action`]="{ item }">
          <base-button-group
            class="full-width"
            :items="menus"
            :value="item"
          ></base-button-group>
        </template>
      </base-serve-table>
    </v-sheet>
    <base-dialog
      v-model="showAdvanced"
      width="75%"
      :min-width="450"
      :max-width="750"
      content-class="raster-advanced-dialog"
      @close="onCloseAdvanced"
    >
      <template #title>Tìm kiếm nâng cao</template>
      <template #content>
        <div
          class="raster-advanced-body d-flex flex-grow-1 overflow-hidden"
          :class="{ 'has-map': advancedTab === 2 }"
        >
          <div class="raster-advanced-filters">
            <v-tabs v-model="advancedTab" dense>
              <v-tab>Thuộc tính</v-tab>
              <v-tab>Metadata</v-tab>
              <v-tab>Không gian</v-tab>
            </v-tabs>
            <v-tabs-items v-model="advancedTab" class="pt-3">
              <v-tab-item>
                <QueryBuilder
                  :key="'attr-' + advancedKey"
                  :layer-id="1"
                  :fields="attributeFields"
                  @update:filters="(val) => (draft.attribute_filters = val)"
                />
              </v-tab-item>
              <v-tab-item>
                <div
                  v-if="!metadataFields.length"
                  class="text-caption grey--text font-italic text-center py-4"
                >
                  Chưa có trường metadata. Hãy tạo nguồn dữ liệu hoặc chọn nguồn
                  khác.
                </div>
                <QueryBuilder
                  v-else
                  :key="
                    'meta-' +
                      advancedKey +
                      '-' +
                      (params.raster_source_id || 'all')
                  "
                  :layer-id="1"
                  :fields="metadataFields"
                  @update:filters="(val) => (draft.metadata_filters = val)"
                />
              </v-tab-item>
              <v-tab-item eager>
                <div class="text-caption grey--text mb-2">
                  Vẽ vùng trên bản đồ hoặc nhập toạ độ (EPSG:4326).
                </div>
                <SpatialFilterTools
                  :key="'spatial-' + advancedKey"
                  ref="spatialTools"
                  :value="draft.spatial_filter"
                  :feature="draft.spatial_feature"
                  @update:spatial="onSpatialDraft"
                  @update:feature="onSpatialFeature"
                  @fit-feature="onFitSpatialFeature"
                />
                <v-divider class="my-3" />
                <div class="d-flex align-center mb-2">
                  <div class="text-caption font-weight-bold grey--text">
                    Vùng bao
                  </div>
                  <v-spacer />
                  <v-btn
                    icon
                    x-small
                    color="error"
                    :disabled="!hasBboxFilter"
                    title="Xóa vùng bao"
                    @click="clearBbox"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </div>
                <v-row dense>
                  <v-col cols="6">
                    <v-text-field
                      v-model.number="draft.bbox.min_lng"
                      dense
                      hide-details
                      outlined
                      label="Min Lng"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model.number="draft.bbox.min_lat"
                      dense
                      hide-details
                      outlined
                      label="Min Lat"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model.number="draft.bbox.max_lng"
                      dense
                      hide-details
                      outlined
                      label="Max Lng"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model.number="draft.bbox.max_lat"
                      dense
                      hide-details
                      outlined
                      label="Max Lat"
                    />
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </div>
          <div v-show="advancedTab === 2" class="raster-advanced-map">
            <RasterDataMap
              v-if="showAdvanced"
              ref="rasterMap"
              @map-loaded="onFilterMapLoaded"
            />
          </div>
          <v-overlay :value="applyingFilter" absolute>
            <v-progress-circular color="primary" indeterminate size="64" />
          </v-overlay>
        </div>
      </template>
      <template #extra-btn>
        <v-btn
          class="mr-2 text-none"
          text
          :disabled="applyingFilter"
          @click="clearAdvanced"
        >
          Xóa lọc
        </v-btn>
        <v-btn
          class="text-none"
          color="primary"
          :loading="applyingFilter"
          :disabled="applyingFilter"
          @click="applyAdvanced"
        >
          Áp dụng
        </v-btn>
      </template>
    </base-dialog>
    <DetailInfo
      ref="dialogDetailInfo"
      :fields="[]"
      :features_count="0"
      @reset="onReset"
    />
  </page-option>
</template>

<script>
import FilterSearch from '@layouts/filter-search.vue'
import QueryBuilder from '@components/Map/modules/AdvancedSearch/QueryBuilder.vue'
import SpatialFilterTools from '@components/Map/modules/AdvancedSearch/SpatialFilterTools.vue'
import DetailInfo from '../datastores/datastore-detail-item/detail-info.vue'
import RasterDataMap from './raster-data-map.vue'
import {
  API_RASTER_DATA,
  API_RASTER_SOURCE,
  API_RASTER_TYPE,
  API_WORKSPACE,
} from '@api'

const EMPTY_DRAFT = () => ({
  attribute_filters: [],
  metadata_filters: [],
  spatial_filter: null,
  spatial_feature: null,
  bbox: {
    min_lng: null,
    min_lat: null,
    max_lng: null,
    max_lat: null,
  },
})

export default {
  name: 'RasterDataMain',
  components: {
    FilterSearch,
    QueryBuilder,
    SpatialFilterTools,
    DetailInfo,
    RasterDataMap,
  },
  data() {
    return {
      showAdvanced: false,
      default_params: {
        sort: '-created_at',
      },
      params: {
        search: '',
        type: null,
        raster_type_id: null,
        raster_source_id: null,
        workspace_id: null,
        attribute_filters: null,
        metadata_filters: null,
        spatial_filter: null,
      },
      draft: EMPTY_DRAFT(),
      advancedTab: 0,
      advancedKey: 0,
      applyingFilter: false,
      pendingFilterClose: false,
      filterLoadStarted: false,
      filterCloseTimer: null,
      tableItems: [],
      selected: null,
      rasterTypes: [],
      rasterSources: [],
      workspaces: [],
      loadingTypes: false,
      loadingSources: false,
      loadingWorkspaces: false,
      typeItems: [
        { text: 'GeoTIFF (.tif)', value: 'tif' },
        { text: 'GeoPDF (.pdf)', value: 'pdf' },
      ],
      attributeFields: [
        { code: 'name', name: 'Tên ảnh' },
        { code: 'type', name: 'Loại (tif/pdf)' },
        { code: 'srid', name: 'Hệ tọa độ (SRID)' },
        { code: 'band_count', name: 'Số band' },
        { code: 'width', name: 'Chiều rộng' },
        { code: 'height', name: 'Chiều cao' },
      ],
      menus: [
        {
          icon: 'mdi-information-outline',
          title: 'Thông tin',
          handler: (item) => this.onShowInfo(item),
        },
      ],
      headers: [
        {
          text: 'Tên ảnh',
          align: 'start',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Loại',
          align: 'start',
          sortable: true,
          value: 'type',
          width: 110,
        },
        {
          text: 'Loại ảnh',
          align: 'start',
          sortable: false,
          value: 'raster_type',
        },
        {
          text: 'Nguồn dữ liệu',
          align: 'start',
          sortable: false,
          value: 'raster_source',
        },
        {
          text: 'Không gian làm việc',
          align: 'start',
          sortable: false,
          value: 'workspace',
        },
        {
          text: 'Metadata',
          align: 'start',
          sortable: false,
          value: 'metadata',
        },
        {
          text: 'Ngày tạo',
          align: 'start',
          sortable: true,
          value: 'created_at',
          width: 160,
        },
        {
          trans: 'field.action',
          value: 'action',
          sortable: false,
          width: '120px',
          align: 'center',
        },
      ],
    }
  },
  computed: {
    api() {
      return API_RASTER_DATA
    },
    metadataFields() {
      const sourceId = this.params.raster_source_id
      const sources = sourceId
        ? this.rasterSources.filter(
            (item) => String(item.id) === String(sourceId)
          )
        : this.rasterSources
      const fields = []
      sources.forEach((source) => {
        const rows = source.metadata || []
        rows.forEach((row) => {
          if (!row || !row.id || !row.field) return
          fields.push({
            code: String(row.id),
            name:
              sources.length > 1 ? `${row.field} (${source.name})` : row.field,
          })
        })
      })
      return fields
    },
    tableParams() {
      const params = {}
      Object.keys(this.params).forEach((key) => {
        const value = this.params[key]
        if (value === null || value === undefined || value === '') return
        if (Array.isArray(value) && value.length === 0) return
        params[key] = value
      })
      return params
    },
    activeAdvancedCount() {
      let count = 0
      if (this.params.attribute_filters) count += 1
      if (this.params.metadata_filters) count += 1
      if (this.params.spatial_filter) count += 1
      return count
    },
    hasBboxFilter() {
      const bbox = this.draft.bbox || {}
      return [bbox.min_lng, bbox.min_lat, bbox.max_lng, bbox.max_lat].some(
        (value) => value !== null && value !== '' && value !== undefined
      )
    },
  },
  created() {
    if (this.showAdvanced === undefined) {
      this.$set(this, 'showAdvanced', false)
    }
    this.fetchLookups()
  },
  watch: {
    advancedTab(val) {
      if (val === 2) {
        this.$nextTick(() => {
          setTimeout(() => this.restoreSpatialDraw(), 250)
        })
      }
    },
  },
  methods: {
    clearBbox() {
      this.draft.bbox = {
        min_lng: null,
        min_lat: null,
        max_lng: null,
        max_lat: null,
      }
      if (
        this.draft.spatial_filter &&
        this.draft.spatial_filter.type === 'bbox'
      ) {
        if (this.$refs.spatialTools && this.$refs.spatialTools.clearDraw) {
          this.$refs.spatialTools.clearDraw()
        } else {
          this.draft.spatial_filter = null
          this.draft.spatial_feature = null
        }
      }
    },
    async fetchLookups() {
      this.loadingTypes = true
      this.loadingSources = true
      this.loadingWorkspaces = true
      try {
        const [types, sources, workspaces] = await Promise.all([
          API_RASTER_TYPE.list({ sort: 'name', paginate: false }),
          API_RASTER_SOURCE.list({ sort: 'name', paginate: false }),
          API_WORKSPACE.list({ sort: 'name', paginate: false }),
        ])
        this.rasterTypes = listFrom(types)
        this.rasterSources = listFrom(sources)
        this.workspaces = listFrom(workspaces)
      } finally {
        this.loadingTypes = false
        this.loadingSources = false
        this.loadingWorkspaces = false
      }
    },
    onItems(items) {
      this.tableItems = items || []
    },
    onTableLoading(isLoading) {
      if (!this.pendingFilterClose) return
      if (isLoading) {
        this.filterLoadStarted = true
        return
      }
      if (!this.filterLoadStarted) return
      this.finishApplyAndClose()
    },
    openAdvanced() {
      this.showAdvanced = true
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.advancedTab === 2) this.restoreSpatialDraw()
        }, 350)
      })
    },
    onCloseAdvanced() {
      this.showAdvanced = false
      this.applyingFilter = false
      this.pendingFilterClose = false
      this.filterLoadStarted = false
      if (this.filterCloseTimer) {
        clearTimeout(this.filterCloseTimer)
        this.filterCloseTimer = null
      }
    },
    resizeAdvancedMap() {
      if (this.$refs.rasterMap && this.$refs.rasterMap.resize) {
        this.$refs.rasterMap.resize()
      }
    },
    onFilterMapLoaded() {
      if (this.advancedTab === 2) this.restoreSpatialDraw()
    },
    restoreSpatialDraw() {
      this.resizeAdvancedMap()
      const tools = this.$refs.spatialTools
      if (!tools || !tools.restoreOnMap) return
      if (
        !tools.originalFeature &&
        !this.draft.spatial_feature &&
        !this.draft.spatial_filter
      ) {
        const bboxFilter = this.buildSpatialFilter()
        if (bboxFilter) tools.spatialData = bboxFilter
      }
      tools.restoreOnMap()
    },
    onFitSpatialFeature(feature) {
      if (this.$refs.rasterMap && this.$refs.rasterMap.fitFeature) {
        this.$refs.rasterMap.fitFeature(feature)
      }
    },
    onShowInfo(item) {
      this.$refs.dialogDetailInfo.open({ ...item })
    },
    onSpatialFeature(feature) {
      this.draft.spatial_feature = feature || null
    },
    onSpatialDraft(value) {
      this.draft.spatial_filter = value
      if (!value) {
        this.draft.spatial_feature = null
      }
      if (value && value.type === 'bbox' && Array.isArray(value.bbox)) {
        this.draft.bbox = {
          min_lng: value.bbox[0],
          min_lat: value.bbox[1],
          max_lng: value.bbox[2],
          max_lat: value.bbox[3],
        }
      }
    },
    applyAdvanced() {
      if (this.applyingFilter) return
      const attribute = (this.draft.attribute_filters || []).filter(
        (item) => item.field && item.operator
      )
      const metadata = (this.draft.metadata_filters || [])
        .filter((item) => item.field && item.operator)
        .map((item) => ({
          id: item.field,
          operator: item.operator,
          value: item.value,
        }))
      const spatial = this.buildSpatialFilter()
      const nextParams = {
        ...this.params,
        attribute_filters: attribute.length ? JSON.stringify(attribute) : null,
        metadata_filters: metadata.length ? JSON.stringify(metadata) : null,
        spatial_filter: spatial ? JSON.stringify(spatial) : null,
      }
      const prevKey = JSON.stringify(this.tableParams)
      this.applyingFilter = true
      this.params = nextParams
      this.$nextTick(() => {
        const nextKey = JSON.stringify(this.tableParams)
        if (prevKey === nextKey) {
          this.finishApplyAndClose()
          return
        }
        this.pendingFilterClose = true
        this.filterLoadStarted = false
        if (this.filterCloseTimer) clearTimeout(this.filterCloseTimer)
        this.filterCloseTimer = setTimeout(() => {
          if (this.pendingFilterClose) this.finishApplyAndClose()
        }, 8000)
      })
    },
    finishApplyAndClose() {
      if (this.filterCloseTimer) {
        clearTimeout(this.filterCloseTimer)
        this.filterCloseTimer = null
      }
      this.pendingFilterClose = false
      this.filterLoadStarted = false
      this.applyingFilter = false
      this.showAdvanced = false
    },
    buildSpatialFilter() {
      if (this.draft.spatial_filter) {
        return this.draft.spatial_filter
      }
      const bbox = this.draft.bbox || {}
      const values = [bbox.min_lng, bbox.min_lat, bbox.max_lng, bbox.max_lat]
      if (
        values.every(
          (value) =>
            value !== null && value !== '' && Number.isFinite(Number(value))
        )
      ) {
        return {
          type: 'bbox',
          bbox: values.map(Number),
        }
      }
      return null
    },
    clearAdvanced() {
      this.draft = EMPTY_DRAFT()
      this.advancedKey += 1
      this.params = {
        ...this.params,
        attribute_filters: null,
        metadata_filters: null,
        spatial_filter: null,
      }
    },
    typeLabel(type) {
      if (type === 'pdf') return 'GeoPDF'
      if (type === 'tif' || type === 'tiff') return 'GeoTIFF'
      return type || '—'
    },
    rasterTypeName(item) {
      return (
        (item.datastore &&
          item.datastore.raster_type &&
          item.datastore.raster_type.name) ||
        ''
      )
    },
    rasterSourceName(item) {
      return (
        (item.datastore &&
          item.datastore.raster_source &&
          item.datastore.raster_source.name) ||
        ''
      )
    },
    metadataRows(item) {
      const sourceRows =
        (item.datastore &&
          item.datastore.raster_source &&
          item.datastore.raster_source.metadata) ||
        []
      const values = (item.datastore && item.datastore.metadata) || []
      const fieldById = {}
      sourceRows.forEach((row) => {
        if (row && row.id) fieldById[String(row.id)] = row.field
      })
      return values
        .filter((row) => row && String(row.value || '').trim() !== '')
        .map((row) => ({
          id: row.id,
          field: fieldById[String(row.id)] || row.field || row.id,
          value: row.value,
        }))
    },
    onReset() {
      if (this.$refs.table) this.$refs.table.reload()
    },
  },
}

function listFrom(res) {
  if (Array.isArray(res.data)) return res.data
  if (res.data && Array.isArray(res.data.list)) return res.data.list
  return []
}
</script>

<style scoped>
.raster-advanced-body {
  min-height: 0;
  height: calc(100vh - 60px - 78px);
  position: relative;
}
.raster-advanced-filters {
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  overflow: auto;
  padding: 12px 16px 8px;
}
.raster-advanced-body.has-map .raster-advanced-filters {
  width: 380px;
  max-width: 380px;
  margin: 0;
  border-right: 1px solid #e0e0e0;
}
.raster-advanced-map {
  flex: 1;
  min-width: 0;
  min-height: 0;
  position: relative;
}
</style>

<style>
.raster-advanced-dialog {
  height: 100%;
}
.raster-advanced-dialog > .v-card {
  height: 100%;
}
</style>
